import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { Formik, Form } from 'formik';
import lodashTimes from 'lodash/fp/times';

import { SR_API_ACTION_APARTMENT_SUBMIT_GUEST_CARD } from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_APARTMENTS,
  DATA_TABLE_VIEW_ID_APARTMENT_BY_ID,
} from 'common/dataTableConstants';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import propertyPricesOptionItems from 'client/helpers/propertyPricesOptionItems';
import bedroomOptionItems from 'client/helpers/bedroomOptionItems';

import {
  dataTable,
  actionDataTableLoadItems,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

import { SCREEN_ID_LOCATOR_TOURS_GUEST_CARD_PAGE } from 'client/constants/screenIds';
import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withApiState from 'client/helpers/withApiState';
import withScrollToTop from 'client/helpers/withScrollToTop';
import postWithState from 'client/actions/postWithState';

import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBackLink from 'client/components/SrBackLink';
import SrLocatorToursNavbar from 'client/components/SrLocatorToursNavbar';
import SrContactUsButton from 'client/components/SrContactUsButton';
import ButtonDeprecated, {
  BUTTON_THEME_YELLOW_GREYCLIFF,
} from 'client/components/ButtonDeprecated';

const formFields = [
  {
    id: 'agentFirstName',
    title: 'First Name',
    validations: ['required'],
    type: 'short_text',
    properties: {
      placeholder: 'Write here...',
    },
  },
  {
    id: 'agentLastName',
    title: 'Last Name',
    validations: ['required'],
    type: 'short_text',
    properties: {
      placeholder: 'Write here...',
    },
  },
  {
    id: 'agentPhone',
    title: 'Phone Number',
    validations: ['phone', 'required'],
    type: 'phone',
    properties: {
      placeholder: 'Write here...',
    },
  },
  {
    id: 'agentEmail',
    title: 'Email',
    validations: ['email', 'required'],
    type: 'email',
    properties: {
      placeholder: 'Write here...',
    },
  },
  {
    id: 'agentLicenceNumber',
    title: 'Real Estate Agent License #',
    validations: ['required'],
    type: 'short_text',
    properties: {
      placeholder: 'Write here...',
    },
  },
  {
    id: 'renterName',
    title: "Client's Name (renter)",
    validations: ['required'],
    type: 'short_text',
    properties: {
      placeholder: 'Write here...',
    },
  },
  {
    id: 'renterPhone',
    title: "Client's Phone (renter)",
    validations: ['phone', 'required'],
    type: 'phone',
    properties: {
      placeholder: 'Write here...',
    },
  },
  {
    id: 'renterEmail',
    title: "Client's Email (renter)",
    validations: ['email', 'required'],
    type: 'email',
    properties: {
      placeholder: 'Write here...',
    },
  },
  {
    id: 'moveInDate',
    title: 'Move-in Date (renter)',
    validations: ['required'],
    type: 'date_picker',
    properties: {
      placeholder: 'Write here...',
    },
  },
  {
    id: 'priceRange',
    title: 'Price Range (renter)',
    validations: ['required'],
    type: 'select',
    properties: {
      placeholder: 'Choose...',
      choices: propertyPricesOptionItems,
    },
  },
  {
    id: 'bedCount',
    title: 'Bed Count (renter)',
    validations: ['required'],
    type: 'select',
    properties: {
      placeholder: 'Choose...',
      choices: bedroomOptionItems,
    },
  },
  {
    id: 'bathsCount',
    title: 'Baths Count (renter)',
    validations: ['required'],
    type: 'select',
    properties: {
      placeholder: 'Choose...',
      choices: lodashTimes(
        (index) => ({
          label: index,
          value: index,
        }),
        4,
      ),
    },
  },
];

function getDataTableSettings({ apartmentId }) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_APARTMENTS,
    viewId: [DATA_TABLE_VIEW_ID_APARTMENT_BY_ID, apartmentId].join(':'),
    count: 1,
    filter: {
      'apartments.id': parseInt(apartmentId, 10),
    },
    itemsPropName: 'apartments',
  };
  return dataTableSettings;
}

@withTransitionHook
@withScreenId(SCREEN_ID_LOCATOR_TOURS_GUEST_CARD_PAGE)
@withApiState({
  getApartmentByIdApiState: {
    action: SR_API_ACTION_APARTMENT_SUBMIT_GUEST_CARD,
  },
})
@dataTable(({ match }) => {
  return getDataTableSettings({
    apartmentId: match.params.apartmentId,
  });
})
@branch({
  guestCardFloorPlanIds: ['locatorTours', 'guestCardFloorPlanIds'],
})
@withScrollToTop
class LocatorToursGuestCardPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Locator Tours',
  });

  static propTypes = {
    guestCardFloorPlanIds: PropTypes.array.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
  };

  static fetchData(match, inject) {
    const dataTableSettings = getDataTableSettings({
      apartmentId: match.params.apartmentId,
    });
    actionInitializeDataTableState(inject, dataTableSettings);
    return actionDataTableLoadItems(inject, dataTableSettings);
  }

  _onGuestCardFormSubmit = (values) => {
    const { guestCardFloorPlanIds, history, match, dispatch } = this.props;
    const apartmentId = parseInt(match.params.apartmentId, 10);

    dispatch(postWithState, {
      action: SR_API_ACTION_APARTMENT_SUBMIT_GUEST_CARD,
      payload: {
        apartmentId: apartmentId,
        floorPlanIds: guestCardFloorPlanIds,
        ...values,
        bedCount: parseInt(values.bedCount, 10),
        bathsCount: parseInt(values.bathsCount, 10),
      },
      onSuccess(locatorTour) {
        history.push(`/locatortours/${match.params.apartmentId}/lockbox?tourId=${locatorTour.id}`);
      },
    });
  };

  render() {
    const { guestCardFloorPlanIds, apartments, match } = this.props;

    if (guestCardFloorPlanIds.length === 0 || apartments.length === 0) {
      return <Redirect to={`/locatortours/${match.params.apartmentId}`} />;
    }

    return (
      <div>
        <SrLocatorToursNavbar withTopPadding cornerNode={<SrContactUsButton />}>
          <SrBackLink to={`/locatortours/${match.params.apartmentId}`}>
            {apartments[0].name}
          </SrBackLink>
        </SrLocatorToursNavbar>
        <SrBox paddingBottom="20px" marginRight={20} marginLeft={20}>
          <SrText font="greycliff" size="40" weight="bolder" leading="45">
            Enter your info
            <br />
            to get the combo
            <br />
            lockbox code
          </SrText>
        </SrBox>
        <SrBox padding="10px 20px 42px 20px">
          <SrText font="greycliff" size="28" lineHeight="35px" weight="bolder">
            This is your digital
            <br />
            guest-card
          </SrText>
        </SrBox>
        <SrBox paddingBottom="200px">
          <Formik
            onSubmit={this._onGuestCardFormSubmit}
            initialValues={createInitialValues({ formFields })}
            validate={createFormValidationFn(formFields)}
            render={(formikBag) => (
              <Form>
                {formFields.map((formField) => (
                  <SrFormikField
                    key={formField.id}
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                    className="margin-left-right-20"
                  />
                ))}
                <SrBox
                  position="fixed"
                  bottom="0"
                  left="0"
                  width="100%"
                  boxShadow="0px -6px 17px rgba(219, 176, 0, 0.14)"
                >
                  <SrBox
                    width="100%"
                    margin="0 auto"
                    paddingBottom="43px"
                    paddingTop="7px"
                    className="greyBlur-90 border-box"
                    paddingRight="10px"
                    paddingLeft="10px"
                  >
                    <ButtonDeprecated
                      theme={BUTTON_THEME_YELLOW_GREYCLIFF}
                      disabled={!formikBag.isValid}
                      type="submit"
                    >
                      Get Combo Lockbox Code
                    </ButtonDeprecated>
                  </SrBox>
                </SrBox>
              </Form>
            )}
          />
        </SrBox>
      </div>
    );
  }
}

LocatorToursGuestCardPage.displayName = 'LocatorToursGuestCardPage';

export default LocatorToursGuestCardPage;
